import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin } from "lucide-react";
import { Counter, Reveal } from "../components/motion-primitives";
import { ABOUT_STATS, MISSION, STORY, VALUES } from "../data/content";

const VALUE_ICONS = {
  shield: "M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z",
  eye: "M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6zm10 2.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z",
  graduation: "M12 4L2 9l10 5 10-5-10-5zm-6 8v4c0 1.5 3 3 6 3s6-1.5 6-3v-4",
  handshake: "M11 17l-1.5 1.5a1.5 1.5 0 01-2.1-2.1L9 14.8 5.8 11.6a1.5 1.5 0 012.1-2.1L11 12.6l3.1-3.1a1.5 1.5 0 012.1 2.1L13 14.8l1.6 1.6a1.5 1.5 0 01-2.1 2.1L11 17z",
  heart: "M12 21s-7.5-4.7-9.5-9A5.4 5.4 0 0112 6.5 5.4 5.4 0 0121.5 12c-2 4.3-9.5 9-9.5 9z",
  bulb: "M9 18h6m-5 3h4m-2-17a6 6 0 00-3.5 10.8c.7.6 1 1.4 1 2.2h5c0-.8.3-1.6 1-2.2A6 6 0 0012 4z",
};

const ValueIcon = ({ name }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
    <path d={VALUE_ICONS[name]} />
  </svg>
);

export default function About() {
  return (
    <div>
      {/* HERO */}
      <section className="bg-section py-20 text-center md:py-24" data-testid="about-hero">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow-pill">Who We Are</span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight md:text-5xl">
              About SkyRoute Freights
            </h1>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              Two decades of moving cargo across borders — built on trust,
              precision, and an unrelenting commitment to our clients.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-1.5 text-xs font-semibold text-foreground" data-testid="about-est">
                <Calendar className="h-3.5 w-3.5 text-brand" />
                Est. 2026
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-1.5 text-xs font-semibold text-foreground" data-testid="about-location">
                <MapPin className="h-3.5 w-3.5 text-brand" />
                Kolkata, West Bengal, India
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-20 md:py-24" data-testid="about-story">
        <div className="container-x grid gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
              Our Story
            </h2>
            <div className="mt-5 h-1 w-16 rounded-full bg-brand" />
          </Reveal>
          <div className="space-y-6 md:col-span-8">
            {STORY.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="leading-relaxed text-muted-foreground">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE SKYROUTE */}
      <section className="bg-section py-20 md:py-24" data-testid="about-stats">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
              Why Choose SkyRoute
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ABOUT_STATS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-border bg-white p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <Counter
                    value={s.value}
                    suffix={s.suffix}
                    className="font-heading text-4xl font-extrabold text-brand"
                  />
                  <h3 className="mt-3 font-heading text-base font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION & VALUES */}
      <section className="py-20 md:py-24" data-testid="about-values">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
              Mission &amp; Values
            </h2>
            <p className="mx-auto mt-4 max-w-3xl leading-relaxed text-muted-foreground">{MISSION}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-border bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <span className="icon-chip-soft" style={{ backgroundColor: "#FEF0DC" }}>
                    <ValueIcon name={v.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="about-cta"
        className="bg-gradient-to-r from-[#F5941D] via-[#F9A93B] to-[#FDEBD3]"
      >
        <div className="container-x py-20 text-center md:py-24">
          <Reveal>
            <h2 className="font-heading text-4xl font-extrabold tracking-tight text-white md:text-5xl">
              Ready to Work Together?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-white/90">
              Let's talk about your freight needs. Our team is ready to build a
              solution that works for your business.
            </p>
            <div className="mt-9 flex justify-center">
              <Link to="/contact" data-testid="about-cta-contact" className="btn-white">
                Get in Touch
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
